import React, { useState, useEffect } from "react";
import { useStore } from "react-redux";
import FemalePatient from "../assets/Patient_Female_96.png";
import MalePatient from "../assets/Patient_Male_96.png";
import styles from "../modals/ResourceViewerModal.module.css";
import { fetchOrganizationByReference } from "../services/organizationService";
import { fetchProviderByReference } from "../services/providerService";
import { resolveName, hydrateFhirGraph } from "../utils/messageUtils";
import { renderReference } from "../utils/renderUtils";
import { calculateAge } from "../utils/dateUtils";


const PatientCardExtended = ({ resource, handleShowNested, fhirClient }) => {
  const store = useStore();
  const { getState } = store;

  const fallbackUrl =
    resource.gender === "female"
      ? FemalePatient
      : resource.gender === "male"
        ? MalePatient
        : null;

  const initialPhotoUrl = resource.photo?.[0]?.url || fallbackUrl;
  const [imgSrc, setImgSrc] = useState(initialPhotoUrl);
  const officialName = resource.name?.find((n) => n.use === "official");
  const maidenName = resource.name?.find((n) => n.use === "maiden");
  const address = resource.address?.[0];
  const geo = address?.extension?.find((e) => e.url.includes("geolocation"))?.extension || [];
  const race = resource.extension?.find((e) => e.url.includes("us-core-race"))?.valueCodeableConcept?.text;
  const ethnicity = resource.extension?.find((e) => e.url.includes("us-core-ethnicity"))?.valueCodeableConcept?.text;
  const birthPlace = resource.extension?.find((e) => e.url.includes("birthPlace"))?.valueAddress;
  const mothersMaidenName = resource.extension?.find((e) => e.url.includes("mothersMaidenName"))?.valueString;
  const deceased = resource.deceasedBoolean ?? resource.deceasedDateTime ?? null;

  const mrn = resource.identifier?.find((id) =>
    id.type?.coding?.some((c) => c.code === "MR")
  )?.value;

  const [managingOrgData, setManagingOrgData] = useState(null);

  useEffect(() => {
    let mounted = true;

    if (!resource || !fhirClient) return;

    hydrateFhirGraph(resource, fhirClient, store);


    const loadData = async () => {
      try {
        //
        // 1. ---- Managing Organization ----
        //
        const orgRef = resource?.managingOrganization?.reference;
        if (orgRef) {
          console.log("Fetching Organization for reference", orgRef);
          await fetchOrganizationByReference(orgRef, fhirClient, store);

          const orgId = orgRef.split("/")[1];
          const org = getState()?.organizations?.[orgId];
          if (mounted) setManagingOrgData(org || null);
        }

        //
        // 2. ---- Providers (generalPractitioner, careProvider, etc) ----
        //
        const providerRefs = [];

        // generalPractitioner: [{ reference: "Practitioner/123" }]
        if (Array.isArray(resource.generalPractitioner)) {
          resource.generalPractitioner.forEach(gp => {
            if (gp.reference) providerRefs.push(gp.reference);
          });
        }

        // careProvider: [{ reference: "Practitioner/999" }]
        if (Array.isArray(resource.careProvider)) {
          resource.careProvider.forEach(cp => {
            if (cp.reference) providerRefs.push(cp.reference);
          });
        }

        // Remove duplicates
        const uniqueRefs = [...new Set(providerRefs)];
        console.log("Fetching providers:", uniqueRefs);

        // Trigger fetch on all
        uniqueRefs.forEach(ref => {
          fetchProviderByReference(ref, fhirClient, store);
        });

      } catch (err) {
        console.error("Failed loading org/providers:", err);
      }
    };

    loadData();
    return () => { mounted = false; };
  }, [resource]);



  return (
    <div className={styles.card}>
      {/* Header */}
      <div className={`${styles.header} mb-4`}>
        <div className={styles.photoContainer}>
          <img
            src={imgSrc}
            alt="Patient Portrait"
            onError={() => {
              if (imgSrc !== fallbackUrl) setImgSrc(fallbackUrl);
            }}
            className={styles.photo}
          />
        </div>

        <div>
          <h3 className="mb-1">
            {officialName?.prefix?.join(" ")} {officialName?.given?.join(" ")}{" "}
            {officialName?.family}
          </h3>

          {mrn && (
            <div className="mb-1 text-muted">
              <strong>MRN:</strong> {mrn}
            </div>
          )}

          {maidenName && (
            <div className="mb-1 text-muted">
              <strong>Maiden Name:</strong>{" "}
              {maidenName.given?.join(" ")} {maidenName.family}
            </div>
          )}

          <div className="mb-1 text-muted">
            <strong>DOB:</strong> {resource.birthDate} &nbsp;|&nbsp;
            <strong>Age:</strong> {calculateAge(resource.birthDate)}
          </div>

          <div className="mb-1 text-muted">
            <strong>Gender:</strong> {resource.gender}
          </div>

          {deceased && (
            <div className="mb-1 text-muted">
              <strong>Deceased:</strong> {String(deceased)}
            </div>
          )}
        </div>
      </div>

      {/* --- IDENTIFIERS --- */}
      <div className={styles.sectionHeader}>Identifiers</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <div className="mb-1">
            <strong>Patient ID (CDR):</strong> {resource.id}
          </div>
          {resource.identifier?.map((id, idx) => (
            <div key={idx} className="mb-1">
              <strong>{id.type?.text || "Other"}:</strong> {id.value}
            </div>
          ))}
        </div></div>

      {/* --- CONTACT --- */}
      <div className={styles.sectionHeader}>Contact</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>

          {resource.contact?.map((c, idx) => (
            <div key={idx} className="mb-2">
              <strong>{c.relationship?.[0]?.text || "Contact"}:</strong>{" "}
              {c.name?.given?.join(" ")} {c.name?.family} ({c.gender})
              <div>
                <strong>Phone:</strong>{" "}
                {c.telecom?.find((t) => t.system === "phone")?.value}
              </div>
              {c.address && (
                <div>
                  <strong>Address:</strong>{" "}
                  {c.address.line?.join(", ")}, {c.address.city},{" "}
                  {c.address.state} {c.address.postalCode},{" "}
                  {c.address.country}
                </div>
              )}
            </div>
          ))}
        </div></div>

      {/* --- LOCATION --- */}
      <div className={styles.sectionHeader}>Location</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {address && (
            <>
              <div className="mb-1">
                <strong>Address:</strong>{" "}
                {address.line?.join(", ")}, {address.city},{" "}
                {address.state} {address.postalCode}, {address.country}
                {geo.length > 0 && (
                  <span className="text-muted">
                    {" "}
                    (Lat: {geo.find((e) => e.url === "latitude")?.valueDecimal},
                    Lon: {geo.find((e) => e.url === "longitude")?.valueDecimal})
                  </span>
                )}
              </div>

              <div className="mb-1">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    `${address.line?.join(", ")}, ${address.city}, ${address.state
                    } ${address.postalCode}, ${address.country}`
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View on Google Maps
                </a>
              </div>
            </>

          )} </div>

        {birthPlace && (
          <div className="mb-1">
            <strong>Birthplace:</strong> {birthPlace.city},{" "}
            {birthPlace.state}, {birthPlace.country}
          </div>
        )}
      </div>

      {/* --- DEMOGRAPHICS --- */}
      <div className={styles.sectionHeader}>Demographics</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          <div className="mb-1">
            <strong>Race:</strong> {race || "Not provided"}
          </div>
          <div className="mb-1">
            <strong>Ethnicity:</strong> {ethnicity || "Not provided"}
          </div>
          <div className="mb-1">
            <strong>Marital Status:</strong> {resource.maritalStatus?.text}
          </div>
          <div className="mb-1">
            <strong>Multiple Birth:</strong>{" "}
            {resource.multipleBirthBoolean ? "Yes" : "No"}
          </div>
          {mothersMaidenName && (
            <div className="mb-1">
              <strong>Mother's Maiden Name:</strong> {mothersMaidenName}
            </div>
          )}
        </div></div>

      {/* --- LANGUAGE --- */}
      <div className={styles.sectionHeader}>Language</div>
      <div className={styles.indented}>
        <div className={styles.sectionContent}>
          {resource.communication?.map((c, idx) => (
            <div key={idx} className="mb-1">
              <strong>Language:</strong> {c.language?.text}{" "}
              {c.preferred ? "(Preferred)" : ""}
            </div>
          ))}
        </div></div>

      {/* --- PROVIDERS --- */}
      <div className={styles.sectionHeader}>Pracitioners</div>
      <div className={styles.indented}>

        {/* General Practitioners */}
        {resource.generalPractitioner?.map((gp, idx) => {
          const gpName = resolveName(gp.reference, store) || gp.display || gp.reference;
          return (
            <div key={idx} className="mb-1">
              <strong>General Practitioner:</strong>{" "}
              {renderReference(gp.reference, gpName, handleShowNested)}
            </div>
          );
        })}

        {/* Managing Organization */}
        {resource.managingOrganization && (() => {
          const orgRef = resource.managingOrganization.reference;
          const orgName = managingOrgData?.name || resolveName(orgRef, store) || orgRef;
          return (
            <div className="mb-1">
              <strong>Managing Organization:</strong>{" "}
              {renderReference(orgRef, orgName, handleShowNested)}
            </div>
          );
        })()}

      </div>

    </div>
  );
};

export default PatientCardExtended;
